import React, { createContext, useContext, useMemo } from 'react';
import {
  getEvents,
  getEventById,
  getEventMetrics,
  addEvent,
  editEvent,
  updateEventPublished,
  updateEventModerationStatus,
  deleteEvent,
  refundEventBooking,
  addEventAttendees,
  deleteEventAttendee,
} from '../services/events';
import {
  getPartnerEvents,
  getPartnerEventById,
  addPartnerEvent,
  editPartnerEvent,
  submitPartnerEvent,
  finalizePartnerEventAttendance,
  archivePartnerEvent,
  deletePartnerEvent,
} from '../services/partnerEvents';
import {
  getPartnerEventStatus as getEventWorkflowStatus,
  isPartnerEventArchived,
  isPartnerEventCompleted,
  canPartnerFinalizeAttendance,
  canPartnerViewAttendance,
  resolvePartnerApprovalStatus,
} from '../components/Partner/Events/partnerEventArchive';
import { isEventDatePast } from '../components/Events/ManageEvent/eventTimeUtils';
import { eventsQueryKeys, partnerEventsQueryKeys } from '../api/query-keys';

export const EVENTS_SCOPE = {
  ADMIN: 'admin',
  PARTNER: 'partner',
};

const adminConfig = {
  scope: EVENTS_SCOPE.ADMIN,
  routes: {
    list: '/events',
    new: '/events/new',
    detail: (eventId) => `/events/${eventId}`,
    edit: (eventId) => `/events/${eventId}/edit`,
  },
  queryKeys: eventsQueryKeys,
  api: {
    getEvents,
    getEventById,
    getEventMetrics,
    addEvent,
    editEvent,
    updateEventPublished,
    updateEventModerationStatus,
    deleteEvent,
    archiveEvent: null,
    refundEventBooking,
    addEventAttendees,
    deleteEventAttendee,
    submitEvent: null,
    finalizeAttendance: null,
  },
  features: {
    showMetrics: true,
    allowRefund: true,
    requiresSaveAs: false,
    publishOnCreate: true,
    hasSettingsStep: true,
    editableStatuses: null,
    createSubmitLabel: 'Publish Event',
    draftSubmitLabel: null,
    createSuccessMessage: 'Event published successfully',
    createErrorMessage: 'Failed to publish event. Please try again.',
  },
};

const partnerConfig = {
  scope: EVENTS_SCOPE.PARTNER,
  routes: {
    list: '/partner-events',
    new: '/partner-events/new',
    detail: (eventId) => `/partner-events/${eventId}`,
    edit: (eventId) => `/partner-events/${eventId}/edit`,
    moderationFeedback: (eventId) => `/partner-events/${eventId}/moderation-feedback`,
  },
  queryKeys: partnerEventsQueryKeys,
  api: {
    getEvents: getPartnerEvents,
    getEventById: getPartnerEventById,
    getEventMetrics: null,
    addEvent: addPartnerEvent,
    editEvent: editPartnerEvent,
    updateEventPublished: null,
    updateEventModerationStatus: null,
    deleteEvent: deletePartnerEvent,
    archiveEvent: archivePartnerEvent,
    refundEventBooking,
    addEventAttendees,
    deleteEventAttendee,
    submitEvent: submitPartnerEvent,
    finalizeAttendance: finalizePartnerEventAttendance,
  },
  features: {
    showMetrics: false,
    allowRefund: true,
    requiresSaveAs: true,
    publishOnCreate: false,
    hasSettingsStep: true,
    editableStatuses: ['draft', 'approved', 'live'],
    createSubmitLabel: 'Submit Event',
    draftSubmitLabel: 'Save as Draft',
    editDraftSubmitLabel: 'Save Changes as Draft',
    editSaveChangesLabel: 'Save Changes',
    createSuccessMessage: 'Event submitted for moderation review',
    draftSuccessMessage: 'Event saved as draft',
    createErrorMessage: 'Failed to submit event. Please try again.',
    draftErrorMessage: 'Failed to save draft. Please try again.',
  },
};

const scopeConfigs = {
  [EVENTS_SCOPE.ADMIN]: adminConfig,
  [EVENTS_SCOPE.PARTNER]: partnerConfig,
};

const EventsScopeContext = createContext(adminConfig);

export const EventsScopeProvider = ({ scope = EVENTS_SCOPE.ADMIN, children }) => {
  const value = useMemo(() => scopeConfigs[scope] ?? adminConfig, [scope]);

  return <EventsScopeContext.Provider value={value}>{children}</EventsScopeContext.Provider>;
};

export const useEventsScope = () => useContext(EventsScopeContext);

export const canEditPartnerEvent = (event, { editableStatuses } = {}) => {
  if (isPartnerEventArchived(event)) return false;
  if (isPartnerEventCompleted(event)) return false;
  if (!editableStatuses?.length) return true;
  const status = resolvePartnerApprovalStatus(event);
  return editableStatuses.includes(status);
};

/** Edit control visible: editable status (per features), not archived/completed; past date blocks except drafts. */
export const canShowPartnerEventEdit = (event, features = {}) => {
  if (!canEditPartnerEvent(event, features)) return false;
  if (resolvePartnerApprovalStatus(event) === 'draft') return true;
  return !isEventDatePast(event?.eventDate);
};

export const canSubmitPartnerEvent = (event) => {
  if (isPartnerEventArchived(event)) return false;
  if (isPartnerEventCompleted(event)) return false;
  const status = getEventWorkflowStatus(event);
  return status === 'draft' || status === 'rejected';
};

export const canFinalizePartnerEventAttendance = canPartnerFinalizeAttendance;

export const canViewPartnerEventAttendance = canPartnerViewAttendance;

export const canPublishEvent = (event, { publishRequiresApproved } = {}) => {
  if (!publishRequiresApproved) return true;
  return getEventWorkflowStatus(event) === 'approved';
};

export { getEventWorkflowStatus };
